import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import { routes } from "~/router";

import FormLabel from "../login/formLabel/FormLabel";
import NotificationBlock from "../notificationBlock/NotificationBlock";
import Switch from "../switch/Switch";
import { CollapsibleSection } from "../ui/collapsibleSection";
import styles from "./SiteSettings.module.scss";

const INTEGRATION_TOOLTIP_ID = "ai-opt-integration-disabled";
const INTEGRATION_TOOLTIP_TEXT =
  "Only available with Full or Easy integration mode.";
const WORDPRESS_REDIRECT_TOOLTIP_TEXT =
  "Not available for WordPress plugin installation. Redirect behavior will inherit your current site default.";

export type AiOptStatus = "null" | "pending" | "rejected" | "approved";

export type AiOptNotification = {
  type: "warning" | "info" | "success" | "danger";
  message: string;
};

export type InjectMarkdownValue = "hidden" | "visible" | "shortcut" | null;

export type AiVisibilityOptimizationsValues = {
  // request/apply
  site_speed_requested: boolean;

  // approved toggles
  prerendering: boolean;
  apex_redirect: boolean;
  inject_widget: boolean;
  inject_markdown: InjectMarkdownValue;
  disable_lazy_automation: boolean;
  disable_defer_automation: boolean;
  disable_preload_automation: boolean;
};

export type AiVisibilityOptimizationsKey =
  keyof AiVisibilityOptimizationsValues;

export type AiVisibilityOptimizationsDisabled = Partial<
  Record<keyof AiVisibilityOptimizationsValues, boolean>
>;

export type AiVisibilityOptimizationsPanelProps = {
  status: AiOptStatus;
  notification?: AiOptNotification;
  instructionText?: string;
  hideInjectWidgetManualAlert?: boolean;
  savedPrerendering?: boolean;
  integrationType?: "full" | "partial" | "edge" | "plugin" | null;
  siteId?: number | string;

  fqdn?: string | null;
  alt_fqdn?: string | null;

  values: AiVisibilityOptimizationsValues;
  disabled?: AiVisibilityOptimizationsDisabled;
  // Hide the prerendering switch when the engine is backend-managed (WordPress
  // onboarding): the caller keeps `values.prerendering` on and out of payloads.
  hidePrerenderingToggle?: boolean;
  // Wizard mode: controls that would render disabled (integration type doesn't
  // support them) are hidden entirely instead.
  hideDisabledControls?: boolean;

  onToggle: <K extends keyof AiVisibilityOptimizationsValues>(
    key: K,
    value: AiVisibilityOptimizationsValues[K],
  ) => void;
};

const INJECT_MARKDOWN_OPTIONS: {
  value: NonNullable<InjectMarkdownValue>;
  label: string;
}[] = [
  { value: "hidden", label: "Off" },
  { value: "visible", label: "Always visible" },
  { value: "shortcut", label: "Shortcut (Ctrl+K)" },
];

export const AiVisibilityOptimizationsPanel = ({
  status,
  notification,
  instructionText,
  hideInjectWidgetManualAlert = false,
  savedPrerendering = false,
  integrationType,
  siteId,
  fqdn,
  alt_fqdn,
  values,
  disabled,
  hidePrerenderingToggle = false,
  hideDisabledControls = false,
  onToggle,
}: AiVisibilityOptimizationsPanelProps) => {
  const showRequest = status === "null";
  const showApproved = status === "approved";
  const prerenderingOff = !values.prerendering;
  // Apex redirect and fine-grained render tweaks need our DNS/cert layer
  // (full or partial). Edge/plugin bypass it.
  const isFullOrPartialIntegration =
    integrationType === "full" || integrationType === "partial";
  // Only attach the "wrong mode" tooltip when an integration has actually
  // been chosen — pre-wizard nulls aren't a "wrong mode".
  const showIntegrationTooltip =
    integrationType === "edge" || integrationType === "plugin";
  const apexRedirectTooltipText =
    integrationType === "plugin"
      ? WORDPRESS_REDIRECT_TOOLTIP_TEXT
      : INTEGRATION_TOOLTIP_TEXT;
  const tooltipAttrs = showIntegrationTooltip
    ? {
        "data-tooltip-id": INTEGRATION_TOOLTIP_ID,
        "data-tooltip-content": INTEGRATION_TOOLTIP_TEXT,
      }
    : {};
  const apexRedirectDisabled =
    prerenderingOff || !isFullOrPartialIntegration || !!disabled?.apex_redirect;
  // Shared gate for the three fine-grained switches (they add only their
  // per-field permission flag on top).
  const fineGrainedDisabled = prerenderingOff || !isFullOrPartialIntegration;

  useEffect(() => {
    if (!isFullOrPartialIntegration && values.apex_redirect) {
      onToggle("apex_redirect", false);
    }
  }, [isFullOrPartialIntegration, values.apex_redirect, onToggle]);

  return (
    <div className={styles.settingsSection}>
      <p className={styles.settingsSectionText}>
        Allows AI search crawlers to view your entire website by serving
        optimized and pre-rendered content.
      </p>

      <div className={styles.settingsSiteSpeedForm}>
        {notification && (
          <>
            <NotificationBlock
              type={notification.type}
              message={notification.message}
            />
            {instructionText ? (
              // @ts-expect-error TS2339 non-existing className
              <p className={styles.switchInstructions}>{instructionText}</p>
            ) : null}
          </>
        )}

        {showRequest && (
          <div className={styles.settingsSwitchGroup}>
            <div>
              <FormLabel label="Submit an Application for AI Visibility Engine Optimization" />
              <p className={styles.settingsSectionText}>
                Serve a pre-rendered, fast and GEO/SEO optimized version of your
                website to bots and crawlers.
              </p>
            </div>
            <Switch
              disabled={!!disabled?.site_speed_requested}
              checked={values.site_speed_requested}
              onChange={(e) =>
                onToggle("site_speed_requested", e.currentTarget.checked)
              }
            />
          </div>
        )}

        {showApproved && (
          <>
            {savedPrerendering && siteId && isFullOrPartialIntegration && (
              <NotificationBlock type="info">
                Note: configure firewall{" "}
                <Link
                  to={routes.site.visibilityEngineSettingsTab({
                    id: String(siteId),
                    tabId: "firewall",
                  })}
                  style={{ fontWeight: 600 }}
                >
                  here
                </Link>
              </NotificationBlock>
            )}
            {!hidePrerenderingToggle && (
              <div className={styles.settingsSwitchGroup}>
                <div>
                  <FormLabel label="Enable AI Visibility Engine Optimization" />
                  <p className={styles.settingsSectionText}>
                    Serve a pre-rendered, fast and GEO/SEO optimized version of
                    your website to bots and crawlers.
                  </p>
                </div>
                <Switch
                  disabled={!!disabled?.prerendering}
                  checked={values.prerendering}
                  onChange={(e) =>
                    onToggle("prerendering", e.currentTarget.checked)
                  }
                />
              </div>
            )}
            {/* Only show redirect switch if apex or www */}
            {alt_fqdn && !(hideDisabledControls && apexRedirectDisabled) ? (
              <div className={styles.settingsSwitchGroup}>
                <div>
                  <FormLabel label={`Redirect ${alt_fqdn}?`} />
                  <p className={styles.settingsSectionText}>
                    {`Your primary domain is ${fqdn}, it is possible to also redirect ${alt_fqdn} to ${fqdn}.`}
                  </p>
                </div>
                <span
                  {...(showIntegrationTooltip
                    ? {
                        "data-tooltip-id": INTEGRATION_TOOLTIP_ID,
                        "data-tooltip-content": apexRedirectTooltipText,
                      }
                    : {})}
                >
                  <Switch
                    disabled={apexRedirectDisabled}
                    checked={values.apex_redirect}
                    onChange={(e) =>
                      onToggle("apex_redirect", e.currentTarget.checked)
                    }
                  />
                </span>
              </div>
            ) : null}

            {values.inject_widget && !hideInjectWidgetManualAlert && (
              <NotificationBlock
                type="info"
                message="Note: If you have installed the snippet manually, it is highly recommended to uninstall it from your pages. Otherwise, the snippet may be included twice."
              />
            )}

            <div className={styles.settingsSwitchGroup}>
              <div>
                <FormLabel label="Inject JS snippet to pages automatically" />
                <p className={styles.settingsSectionText}>
                  Enables seamless addition of dynamic features to pages
                </p>
              </div>
              <Switch
                disabled={prerenderingOff || !!disabled?.inject_widget}
                checked={values.inject_widget}
                onChange={(e) =>
                  onToggle("inject_widget", e.currentTarget.checked)
                }
              />
            </div>

            <div className={styles.settingsSwitchGroup}>
              <div>
                <FormLabel label="AI Content Preview" />
                <p className={styles.settingsSectionText}>
                  Display a floating toggle on your pages that lets visitors
                  switch between the regular view and the AI-optimized version
                  of the content.
                </p>
              </div>
              <div className={styles.inlineRadioGroup}>
                {INJECT_MARKDOWN_OPTIONS.map((opt) => (
                  <label key={opt.value} className={styles.inlineRadioLabel}>
                    {opt.label}
                    <input
                      type="radio"
                      name="inject_markdown"
                      className={styles.inlineRadioInput}
                      checked={values.inject_markdown === opt.value}
                      disabled={prerenderingOff || !!disabled?.inject_markdown}
                      onChange={() => onToggle("inject_markdown", opt.value)}
                    />
                  </label>
                ))}
              </div>
            </div>

            {!(hideDisabledControls && fineGrainedDisabled) && (
              <CollapsibleSection title="Fine-grained settings">
                <div className={styles.settingsSiteSpeedForm}>
                  <div className={styles.settingsSwitchGroup}>
                    <div>
                      <FormLabel label="Disable Lazy Loading" />
                      <p className={styles.settingsSectionText}>
                        AI Visibility Engine identifies and instructs the
                        browsers to load offscreen images lazily, allowing more
                        important resources to load first
                      </p>
                    </div>
                    <span {...tooltipAttrs}>
                      <Switch
                        disabled={
                          fineGrainedDisabled ||
                          !!disabled?.disable_lazy_automation
                        }
                        checked={values.disable_lazy_automation}
                        onChange={(e) =>
                          onToggle(
                            "disable_lazy_automation",
                            e.currentTarget.checked,
                          )
                        }
                      />
                    </span>
                  </div>

                  <div className={styles.settingsSwitchGroup}>
                    <div>
                      <FormLabel label="Disable Script Deferring" />
                      <p className={styles.settingsSectionText}>
                        AI Visibility Engine identifies and defers loading of
                        some external scripts. Disable if pages do not display
                        or function properly
                      </p>
                    </div>
                    <span {...tooltipAttrs}>
                      <Switch
                        disabled={
                          fineGrainedDisabled ||
                          !!disabled?.disable_defer_automation
                        }
                        checked={values.disable_defer_automation}
                        onChange={(e) =>
                          onToggle(
                            "disable_defer_automation",
                            e.currentTarget.checked,
                          )
                        }
                      />
                    </span>
                  </div>

                  <div className={styles.settingsSwitchGroup}>
                    <div>
                      <FormLabel label="Disable Resources Preloading" />
                      <p className={styles.settingsSectionText}>
                        AI Visibility Engine identifies and instructs the
                        browsers to preload the most critical resources
                      </p>
                    </div>
                    <span {...tooltipAttrs}>
                      <Switch
                        disabled={
                          fineGrainedDisabled ||
                          !!disabled?.disable_preload_automation
                        }
                        checked={values.disable_preload_automation}
                        onChange={(e) =>
                          onToggle(
                            "disable_preload_automation",
                            e.currentTarget.checked,
                          )
                        }
                      />
                    </span>
                  </div>
                </div>
              </CollapsibleSection>
            )}
          </>
        )}
      </div>
      {showIntegrationTooltip && <Tooltip id={INTEGRATION_TOOLTIP_ID} />}
    </div>
  );
};
